import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import React from "react";
import { IEvent } from "modules/event/types";
import { ICompanyServicePlan } from "modules/company/types";
import { useStyles } from "./styles";

interface Props {
  events: IEvent[];
  beforeUpgrade?: boolean;
  prevCompanyPlan: ICompanyServicePlan;
  myCompanyPlan: ICompanyServicePlan;
}

const PlanDowngradePopup = ({
  events,
  beforeUpgrade = false,
  myCompanyPlan: { settings },
  prevCompanyPlan: { settings: prevSettings },
}: Props): JSX.Element => {
  const { t } = useI18n(["common", "settings"]);
  const styles = useStyles();

  const changes = React.useMemo(() => {
    if (!prevSettings || !settings) {
      return {};
    }

    return {
      isCustomBrandingRemoved:
        !settings.company.whiteLabelling && prevSettings.company.whiteLabelling,
      isExitUrlRemoved: !settings.event.exitPage && prevSettings.event.exitPage,
      isManagerLimitReduced:
        settings.company.managers > -1 &&
        settings.company.managers < prevSettings.company.managers,
      isEventAssistantsReduced:
        settings.company.assistants !== undefined &&
        prevSettings.company.assistants !== undefined &&
        settings.company.assistants < prevSettings.company.assistants,
      isNoOfFloorsReduced:
        settings.theater.maxCapacity < prevSettings.theater.maxCapacity,
      isGuestPerTableReduced:
        settings.theater.maxInTable < prevSettings.theater.maxInTable,
      isSpeakersOnStageReduced:
        settings.theater.speakersOnStage < prevSettings.theater.speakersOnStage,
      isMaxDurationReduced:
        settings.event.timeLimit > -1 &&
        settings.event.timeLimit < prevSettings.event.timeLimit,
      isConcurrentLimitReduced:
        settings.event.concurrent > -1 &&
        settings.event.concurrent < prevSettings.event.concurrent,
      isSponsorsLimitReduced:
        settings.event.sponsors < prevSettings.event.sponsors,
    };
  }, [prevSettings, settings]);

  const getBeforeUpgradeContent = () => (
    <>
      <Typography variant="body1">
        {t("settings:downgrade.capacity.reduce")}
      </Typography>

      <ul>
        {events.map((event) => (
          <li>{event.name}</li>
        ))}
      </ul>

      <Typography variant="body1">
        {t("settings:proceed.aspects.change")}
      </Typography>

      {changes.isCustomBrandingRemoved && (
        <Box>
          <Typography variant="h6">
            {t("settings:top.bar.event.branding")}
          </Typography>
          <Typography variant="body1">
            {t("settings:top.bar.event.branding.denied")}
          </Typography>
        </Box>
      )}
      {changes.isExitUrlRemoved && (
        <Box>
          <Typography variant="h6">{t("settings:custom.exit.url")}</Typography>
          <Typography variant="body1">
            {t("settings:exit.page.customization.denied")}
          </Typography>
        </Box>
      )}
      {changes.isManagerLimitReduced && (
        <Box>
          <Typography variant="h6">{t("settings:event.managers")}</Typography>
          <Typography variant="body1">
            {t("settings:event.manager.denied")}
          </Typography>
        </Box>
      )}

      {changes.isNoOfFloorsReduced && (
        <Box>
          <Typography variant="h6">{t("settings:number.floors")}</Typography>
          <Typography variant="body1">
            {t("settings:same.floor.capacity")}
          </Typography>
        </Box>
      )}

      {changes.isGuestPerTableReduced && (
        <Box>
          <Typography variant="h6">{t("settings:guests.table")}</Typography>
          <Typography variant="body1">
            {t("settings:small.table.capacity")}
            <br />
            {t("settings:note.table.capacity")}
          </Typography>
        </Box>
      )}

      {changes.isSpeakersOnStageReduced && (
        <Box>
          <Typography variant="h6">{t("settings:speakers.onstage")}</Typography>
          <Typography variant="body1">
            {t("settings:downgrade.speakers")}
          </Typography>
        </Box>
      )}

      {changes.isEventAssistantsReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:count.event.level.assistants")}
          </Typography>
          <Typography variant="body1">
            {t("settings:downgrade.event.level.assistants")}
          </Typography>
        </Box>
      )}

      {changes.isMaxDurationReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:max.event.duration")}
          </Typography>
          <Typography variant="body1">
            {t("settings:plan.uprade.text")}
          </Typography>
        </Box>
      )}

      {changes.isConcurrentLimitReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:events.simultaneous")}
          </Typography>
          <Typography variant="body1">
            {t("settings:fewer.simultaneous.events")}
          </Typography>
        </Box>
      )}

      {changes.isSponsorsLimitReduced && (
        <Box>
          <Typography variant="h6">{t("settings:sponsor")}</Typography>
          <Typography variant="body1">
            {t("settings:final.warning.downgrade.plan", {
              interpolation: false.valueOf,
            })}
          </Typography>
        </Box>
      )}
    </>
  );

  const getAfterUpgradeContent = () => (
    <>
      <Typography variant="body1">{t("settings:downgrade.success")}</Typography>

      <Typography variant="body1">
        {t("settings:downgrade.features.affected")}
      </Typography>
      <ul>
        {events.map((event) => (
          <li>{event.name}</li>
        ))}
      </ul>

      <Typography variant="body1">
        {t("settings:following.aspects.change")}
      </Typography>

      {changes.isCustomBrandingRemoved && (
        <Box>
          <Typography variant="h6">
            {t("settings:top.bar.event.branding")}
          </Typography>
          <Typography variant="body1">
            {t("settings:downgraded.top.bar.event.branding")}
          </Typography>
        </Box>
      )}
      {changes.isExitUrlRemoved && (
        <Box>
          <Typography variant="h6">{t("settings:custom.exit.url")}</Typography>
          <Typography variant="body1">
            {t("settings:downgraded.custom.url")}
          </Typography>
        </Box>
      )}
      {changes.isManagerLimitReduced && (
        <Box>
          <Typography variant="h6">{t("settings:event.managers")}</Typography>
          <Typography variant="body1">
            {t("settings:downgraded.event.manager")}
          </Typography>
        </Box>
      )}

      {changes.isNoOfFloorsReduced && (
        <Box>
          <Typography variant="h6">{t("settings:number.floors")}</Typography>
          <Typography variant="body1">
            {t("settings:downgraded.number.floors")}
          </Typography>
        </Box>
      )}

      {changes.isGuestPerTableReduced && (
        <Box>
          <Typography variant="h6">{t("settings:guests.table")}</Typography>
          <Typography variant="body1">
            {t("settings:downgrade.plan.smaller.number.guests")}
          </Typography>
        </Box>
      )}

      {changes.isSpeakersOnStageReduced && (
        <Box>
          <Typography variant="h6">{t("settings:speakers.onstage")}</Typography>
          <Typography variant="body1">
            {t("settings:downgrade.speakers.ontage")}
          </Typography>
        </Box>
      )}

      {changes.isEventAssistantsReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:count.event.level.assistants")}
          </Typography>
          <Typography variant="body1">
            {t("settings:downgraded.count.event.level.assistants")}
          </Typography>
        </Box>
      )}

      {changes.isMaxDurationReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:ax.event.duration")}
          </Typography>
          <Typography variant="body1">
            {t("settings:downgrade.plan.warnings", {
              interpolations: false,
            })}
          </Typography>
        </Box>
      )}

      {changes.isConcurrentLimitReduced && (
        <Box>
          <Typography variant="h6">
            {t("settings:events.simultaneous")}
          </Typography>
          <Typography variant="body1">
            {t("settings:downgrade.plan.text")}
          </Typography>
        </Box>
      )}

      {changes.isSponsorsLimitReduced && (
        <Box>
          <Typography variant="h6">{t("settings:sponsor")}</Typography>
          <Typography variant="body1">
            {t("settings:downgrade.plan.warning")}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box className={styles.pdpBox}>
      {beforeUpgrade ? getBeforeUpgradeContent() : getAfterUpgradeContent()}
    </Box>
  );
};

export default PlanDowngradePopup;
